/**
 * Utilidades de formateo para la aplicación
 */

/**
 * Formatea un número como precio en formato argentino
 * Ejemplo: 10300.5 -> "$10.300,50"
 *
 * @param value - Número o string a formatear
 * @param showCents - Si mostrar centavos (default: true)
 * @returns Precio formateado con signo peso
 */
export function formatPrice(value: number | string | null | undefined, showCents: boolean = true): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '-';
  }

  // Formato argentino: punto para miles, coma para decimales
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  };

  const formatted = num.toLocaleString('es-AR', options);
  return `$${formatted}`;
}

/**
 * Formatea un número como porcentaje
 * Ejemplo: 0.15 -> "15%"
 *
 * @param value - Número decimal (0.15 = 15%)
 * @param decimals - Cantidad de decimales (default: 0)
 */
export function formatPercent(value: number | null | undefined, decimals: number = 0): string {
  if (value === null || value === undefined) {
    return '-';
  }

  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Formatea una fecha en formato argentino
 * Ejemplo: "2024-01-15" -> "15/01/2024"
 *
 * IMPORTANTE: Las fechas en formato "YYYY-MM-DD" (solo fecha, sin hora) se parsean
 * como hora local para evitar problemas de timezone donde "2024-01-20" se mostraría
 * como "19/01/2024" al convertir de UTC a hora local.
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) {
    return '-';
  }

  let d: Date;

  if (typeof date === 'string') {
    // Si es formato "YYYY-MM-DD" (solo fecha, sin hora ni timezone)
    // parseamos manualmente para evitar problemas de timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      d = new Date(year, month - 1, day); // month es 0-indexed
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }

  if (isNaN(d.getTime())) {
    return '-';
  }

  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formatea fecha y hora
 * Ejemplo: "2024-01-15T10:30:00" -> "15/01/2024, 10:30"
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) {
    return '-';
  }

  const d = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(d.getTime())) {
    return '-';
  }

  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
